// src/pages/app/BotCreatePage.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createBot, CreateBotPayload, Bot } from "../../api/bots";
import { useTranslation } from "react-i18next";

const BotCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState<{
    name: string;
    slug: string;
    description: string;
  }>({
    name: "",
    slug: "",
    description: ""
  });

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setForm((prev) => ({
        ...prev,
        [field]: value
      }));
    };

  const handleSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const payload: CreateBotPayload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || undefined
      };

      const bot: Bot = await createBot(payload);

      // After creating, send user directly to Features & Plan
      navigate(`/app/bots/${bot.id}/features`);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botCreate.errors.createFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t("botCreate.title")}</h1>
      </div>

      <form className="form" onSubmit={handleSubmit}>
        {error && <div className="form-error">{error}</div>}

        <p className="muted">{t("botCreate.subtitle")}</p>

        <label className="form-field">
          <span>{t("botCreate.fields.name")}</span>
          <input
            type="text"
            value={form.name}
            onChange={handleChange("name")}
            required
          />
        </label>

        <label className="form-field">
          <span>{t("botCreate.fields.slug")}</span>
          <input
            type="text"
            value={form.slug}
            onChange={handleChange("slug")}
            required
          />
          <small>{t("botCreate.fields.slugHelp")}</small>
        </label>

        <label className="form-field">
          <span>{t("botCreate.fields.description")}</span>
          <textarea
            value={form.description}
            onChange={handleChange("description")}
            rows={2}
          />
        </label>

        <button className="btn-primary" type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? t("botCreate.actions.creating")
            : t("botCreate.actions.create")}
        </button>
      </form>
    </div>
  );
};

export default BotCreatePage;
